import { useEffect, useState } from 'react';
import { getEdmDashboardStats } from '../../../../helpers/api';
import { Box, Card, Grid2, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CorporateFare, CheckCircle, Cancel } from '@mui/icons-material'; // Modern MUI Icons replacing Antd
import LoaderSkeleton from '../../../common/LoaderSkeleton';

// 1. REUSABLE STAT CARD (Same beautiful UI from your other dashboard)
const StatCard = ({ title, value, subtitle, icon: IconComponent, color, type = '' }) => {
  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(145deg, #ffffff, #fafbff)',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.08)',
          borderColor: alpha(color, 0.3),
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography
            variant="overline"
            color="textSecondary"
            sx={{ fontWeight: 600, letterSpacing: 1 }}
          >
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.5 }}>
            {value}
            <Typography component="span" variant="h5" sx={{ ml: 0.5, color: 'text.secondary' }}>
              {type}
            </Typography>
          </Typography>
        </Box>

        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '14px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: alpha(color, 0.1),
            color: color,
          }}
        >
          <IconComponent fontSize="large" />
        </Box>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
        {subtitle}
      </Typography>
    </Card>
  );
};

export default function StatCardsRegulatorView({ stateId }) {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [stateId]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await getEdmDashboardStats(stateId);
      console.log('response', response);
      const responseData = response?.result?.data;
      setStatsData(responseData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to format big numbers (e.g., 1200 -> 1,200)
  const formatNum = (num) => (num ? Intl.NumberFormat('en-NG').format(num) : 0);

  // 3. RENDER LOGIC
  if (loading) {
    return (
      <Grid2 container spacing={3}>
        {[1, 2, 3].map((item) => (
          <Grid2 key={item} size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
            <Box sx={{ p: 2, height: '100%', bgcolor: '#fff', borderRadius: '16px' }}>
              <LoaderSkeleton />
            </Box>
          </Grid2>
        ))}
      </Grid2>
    );
  }

  // 4. UI CONFIGURATION
  const cardConfigs = [
    {
      title: 'Facilities',
      value: formatNum(statsData?.facilityCount),
      subtitle: 'Total No. of Facilities',
      icon: CorporateFare,
      color: '#1976d2', // Enterprise Blue
    },
    {
      title: 'Active',
      value: formatNum(statsData?.activeFacilityCount),
      subtitle: 'Total No. of Active Facilities',
      icon: CheckCircle,
      color: '#00ab55', // Success Green
    },
    {
      title: 'Inactive',
      value: formatNum(statsData?.inActiveFacilityCount),
      subtitle: 'Total No. of Inactive Facilities',
      icon: Cancel,
      color: '#ff4842', // Error Red
    },
  ];

  return (
    <Box>
      <Grid2 container spacing={3} alignItems="stretch">
        {cardConfigs.map((card, index) => (
          <Grid2 key={index} size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
            <StatCard
              title={card.title}
              value={card.value}
              subtitle={card.subtitle}
              icon={card.icon}
              color={card.color}
              type={card.type}
            />
          </Grid2>
        ))}
      </Grid2>
    </Box>
  );
}
